import { LoggerInstance } from '@easeltv/logger';
import {
    remote,
    RequestBuilder,
    RequestBuilderOptions,
    RequestResult,
} from '@easeltv/remote';
import { ZodTypeDef } from 'zod';

import { CreateSessionBody, Session } from '../models';

/* ------------------------ DEFAULT_REPORTER_FUNCTION ----------------------- */

const DEFAULT_REPORTER_FUNCTION =
    (logger?: LoggerInstance) => (result: RequestResult<unknown>) => {
        if (result.payload?.status && result.payload.status > 499)
            logger?.error(result);
    };

/* ----------------------------- SDKWithSession ----------------------------- */

/**
 * SDKWithSession
 *
 * This class is aware of the session and baseUrl and abstracts a lot of the boilerplate code around making API requests
 */
export class SDKWithSession {
    /** A token requested from the api which is required to make requests for content from the API */
    session?: string;

    /** A token requested from the api which is required to make requests for content from the API */
    baseUrl: string;

    /** Needed to make the initial session and config requests */
    apiKey: string;

    refreshTokenStorage: {
        get: () => string | undefined;
        set: (token: string) => void;
    };

    /** Tenant injected into the tenant header */
    tenant: string;

    logger: LoggerInstance | undefined;

    constructor(
        baseUrl: string,
        tenant: string,
        apiKey: string,
        refreshTokenStorage: {
            get: () => string | undefined;
            set: (token: string) => void;
        },
        logger?: LoggerInstance,
    ) {
        this.baseUrl = baseUrl;
        this.apiKey = apiKey;
        this.refreshTokenStorage = refreshTokenStorage;
        this.tenant = tenant;
        this.logger = logger;
    }

    getSession = () => this.session;

    setSession = (session: string) => {
        this.session = session;
    };

    getTenant = () => this.tenant;

    setTenant = (tenant: string) => {
        this.tenant = tenant;
    };

    /**
     * createSession
     *
     * Uses the API key provided on initialization to fetch a session token to make future requests with
     *
     * @returns A Session object
     */
    createSession = (body: CreateSessionBody) =>
        remote
            .post('/v1/session', this.baseUrl, { validator: Session })
            .withHeaders({
                'api-key': this.apiKey,
                'Content-Type': 'application/json',
                tenant: this.getTenant(),
            })
            .withBody(body)
            .send();

    /**
     * initialize
     *
     * If an existing session token exists, it can be provided here for all future requests to use.
     * If no session token is provided, one will be requested from the API.
     *
     * @param refreshToken - A refreshToken string
     * @returns A SessionToken object
     */
    initialize = async () => {
        const refreshToken = this.refreshTokenStorage.get();
        const body = refreshToken ? { refreshToken } : {};

        const result = await this.createSession(body);

        if (!result.ok) return result;

        this.setSession(result.payload.body.id);

        if (result.payload.body.refreshToken)
            this.refreshTokenStorage.set(result.payload.body.refreshToken);

        return result;
    };

    handleUnAuthFail = async <
        Output,
        Def extends ZodTypeDef = ZodTypeDef,
        Input = Output,
    >(
        builder: RequestBuilder<Output, Def, Input>,
    ) => {
        const result = await this.initialize();

        if (!result.ok) return result;

        builder.withHeaders({
            session: result.payload.body.id,
            tenant: this.getTenant(),
        });

        return result;
    };

    /* --------------------------- Convenience Methods -------------------------- */

    /**
     * get
     *
     * Wrapper around the remote request convenance methods to include repeated boilerplate code.
     *
     * @param url - The location to send the request
     * @param options - Options to add to the request, can be added later by using chain methods
     *
     * @returns An instance of the RequestBuilder
     */
    get = <Output, Def extends ZodTypeDef = ZodTypeDef, Input = Output>(
        url: string,
        opts: RequestBuilderOptions<Output, Def, Input>,
    ): RequestBuilder<Output, Def, Input> =>
        remote
            .get(url, this.baseUrl, opts)
            .withHeaders({
                ...(this.getSession() ? { session: this.getSession() } : {}),
                tenant: this.getTenant(),
            })
            .withReporter(DEFAULT_REPORTER_FUNCTION(this.logger))
            .withUnAuthRetry(this.handleUnAuthFail);

    /**
     * head
     *
     * Wrapper around the remote request convenance methods to include repeated boilerplate code.
     *
     * @param url - The location to send the request
     * @param options - Options to add to the request, can be added later by using chain methods
     *
     * @returns An instance of the RequestBuilder
     */
    head = <Output, Def extends ZodTypeDef = ZodTypeDef, Input = Output>(
        url: string,
        opts: RequestBuilderOptions<Output, Def, Input>,
    ): RequestBuilder<Output, Def, Input> =>
        remote
            .head(url, this.baseUrl, opts)
            .withHeaders({
                ...(this.getSession() ? { session: this.getSession() } : {}),
                tenant: this.getTenant(),
            })
            .withReporter(DEFAULT_REPORTER_FUNCTION(this.logger))
            .withUnAuthRetry(this.handleUnAuthFail);

    /**
     * post
     *
     * Wrapper around the remote request convenance methods to include repeated boilerplate code.
     *
     * @param url - The location to send the request
     * @param options - Options to add to the request, can be added later by using chain methods
     *
     * @returns An instance of the RequestBuilder
     */
    post = <Output, Def extends ZodTypeDef = ZodTypeDef, Input = Output>(
        url: string,
        opts: RequestBuilderOptions<Output, Def, Input>,
    ): RequestBuilder<Output, Def, Input> =>
        remote
            .post(url, this.baseUrl, opts)
            .withHeaders({
                ...(this.getSession() ? { session: this.getSession() } : {}),
                tenant: this.getTenant(),
            })
            .withReporter(DEFAULT_REPORTER_FUNCTION(this.logger))
            .withUnAuthRetry(this.handleUnAuthFail);

    /**
     * put
     *
     * Wrapper around the remote request convenience methods to include repeated boilerplate code.
     *
     * @param url - The location to send the request
     * @param options - Options to add to the request, can be added later by using chain methods
     *
     * @returns An instance of the RequestBuilder
     */
    put = <Output, Def extends ZodTypeDef = ZodTypeDef, Input = Output>(
        url: string,
        opts: RequestBuilderOptions<Output, Def, Input>,
    ): RequestBuilder<Output, Def, Input> =>
        remote
            .put(url, this.baseUrl, opts)
            .withHeaders({
                ...(this.getSession() ? { session: this.getSession() } : {}),
                tenant: this.getTenant(),
            })
            .withReporter(DEFAULT_REPORTER_FUNCTION(this.logger))
            .withUnAuthRetry(this.handleUnAuthFail);

    /**
     * del
     *
     * Wrapper around the remote request convenance methods to include repeated boilerplate code.
     *
     * @param url - The location to send the request
     * @param options - Options to add to the request, can be added later by using chain methods
     *
     * @returns An instance of the RequestBuilder
     */
    del = <Output, Def extends ZodTypeDef = ZodTypeDef, Input = Output>(
        url: string,
        opts: RequestBuilderOptions<Output, Def, Input>,
    ): RequestBuilder<Output, Def, Input> =>
        remote
            .del(url, this.baseUrl, opts)
            .withHeaders({
                ...(this.getSession() ? { session: this.getSession() } : {}),
                tenant: this.getTenant(),
            })
            .withReporter(DEFAULT_REPORTER_FUNCTION(this.logger))
            .withUnAuthRetry(this.handleUnAuthFail);

    /**
     * options
     *
     * Wrapper around the remote request convenance methods to include repeated boilerplate code.
     *
     * @param url - The location to send the request
     * @param options - Options to add to the request, can be added later by using chain methods
     *
     * @returns An instance of the RequestBuilder
     */
    options = <Output, Def extends ZodTypeDef = ZodTypeDef, Input = Output>(
        url: string,
        opts: RequestBuilderOptions<Output, Def, Input>,
    ): RequestBuilder<Output, Def, Input> =>
        remote
            .options(url, this.baseUrl, opts)
            .withHeaders({
                ...(this.getSession() ? { session: this.getSession() } : {}),
                tenant: this.getTenant(),
            })
            .withReporter(DEFAULT_REPORTER_FUNCTION(this.logger))
            .withUnAuthRetry(this.handleUnAuthFail);

    /**
     * patch
     *
     * Wrapper around the remote request convenance methods to include repeated boilerplate code.
     *
     * @param url - The location to send the request
     * @param options - Options to add to the request, can be added later by using chain methods
     *
     * @returns An instance of the RequestBuilder
     */
    patch = <Output, Def extends ZodTypeDef = ZodTypeDef, Input = Output>(
        url: string,
        opts: RequestBuilderOptions<Output, Def, Input>,
    ): RequestBuilder<Output, Def, Input> =>
        remote
            .patch(url, this.baseUrl, opts)
            .withHeaders({
                ...(this.getSession() ? { session: this.getSession() } : {}),
                tenant: this.getTenant(),
            })
            .withReporter(DEFAULT_REPORTER_FUNCTION(this.logger))
            .withUnAuthRetry(this.handleUnAuthFail);
}
